/**
 * AOI 生产管理平台 V2 - 集中常量 (几何限制 / zoom 阈值 / 缓存上限 / 颜色表 / 错误文案)。
 *
 * <p>API key / token 复用旧目录 aoi-platform/constants.ts (同一个 key, 不重复硬编码)。
 */

export {
    GOOGLE_MAPS_API_KEY,
    MAX_LIST_SIZE,
} from '../aoi-platform/constants';

/* ====================== 几何约束 (跟后端保持一致) ====================== */

/** 多边形最少顶点数 (不含闭合重复点)。 */
export const MIN_VERTICES = 3;

/*
 * 顶点数上限 (2026-09-18) 前端不再限制: 后端走动态配置 `map.aoi-produce.config.maxVertices` (60 秒热生效),
 * 前端再写死一份等于把配置项变回"要发版才能调"。超限由后端 VERTEX_LIMIT 拦, 文案直接用后端返回的
 * errorMessage (带当时生效的范围与当前顶点数), 见 PRODUCE_ERROR_MESSAGES 的说明。
 */

/** 1 级 AOI 面积上限 (m²) = 2 km²。 */
export const AREA_LIMIT_LEVEL_1_SQM = 2_000_000;

/** 2 级 AOI 面积上限 (m²) = 15 km²。 */
export const AREA_LIMIT_LEVEL_2_SQM = 15_000_000;

/* ====================== 门禁码 ====================== */

/** 门禁类型下拉 (跟旧详情页保持一致的 code 取值, 便于历史数据对得上)。 */
export const ACCESS_CODE_TYPE_OPTIONS = [
    { value: 'Gate', label: '社区门禁' },
    { value: 'MainDoor', label: '楼门门禁' },
    { value: 'SideDoor', label: '侧门门禁' },
    { value: 'Elevate', label: '电梯门禁' },
    { value: 'Garage', label: '车库门禁' },
    { value: 'ParcelStop', label: '快递柜' },
];

/** 门禁状态下拉 (值 = DB 枚举名, 后端原样存 access_codes.available)。 */
export const ACCESS_CODE_STATUS_OPTIONS = [
    { value: 'PENDING_VERIFICATION', label: '待验证' },
    { value: 'AVAILABLE', label: '可用' },
    { value: 'UNAVAILABLE', label: '不可用' },
];

/* ====================== 瓦片加载 ====================== */

/** 瓦片端点 (api-server REST, 不是 GraphQL —— 瓦片要能按 URL 被浏览器缓存)。 */
export const AOI_TILE_URL = '/api/aoi/tiles/{z}/{x}/{y}.mvt';

/**
 * 瓦片像素尺寸。
 * 512 配 zoomOffset -1: 覆盖一屏从 ~15 片降到 ~6 片, 请求数降到 1/3, 代价是单片大 4 倍。
 */
export const TILE_SIZE = 512;

/** 配合 TILE_SIZE=512: 地图 zoom N 请求瓦片 z = N-1。 */
export const TILE_ZOOM_OFFSET = -1;

/**
 * 同时在飞的瓦片请求上限。瓦片是渐进渲染的, 排队只影响画满速度, 不影响可用性。
 *
 * <p>3 → 6 (2026-08-03): 上线实测服务端 p50=13ms / p95=39ms (398 片), 20 人同时拖 × 6 并发
 * 也只占 PostGIS ~1 个核, 当初压到 3 的担心不成立; 放到 6 首屏铺满时间减半。
 */
export const TILE_MAX_CONCURRENCY = 6;

/** zoom 低于该值不加载 AOI (对应后端 MIN_TILE_Z=13, 地图 zoom = 瓦片 z + 1)。 */
export const MIN_LOAD_ZOOM = 14;

/**
 * 该地图 zoom 起显示 1 级楼栋面; 低于它只显示 2 级聚合面。
 * 取 17 与 Google 底图一致 (Google 也是 17 才渲染建筑轮廓), 楼和底图对得上。
 * 对应后端 BUILDING_MIN_TILE_Z=16。改这个值必须两端一起改。
 */
export const BUILDING_MIN_ZOOM = 17;

/* ====================== 默认视图 ====================== */

/** 洛杉矶市中心 - 进页面 / 重置时的地图中心。 */
export const LA_CENTER = { lat: 34.0522, lng: -118.2437 };

/** 默认 zoom (进页面即此值; 低于 MIN_LOAD_ZOOM, 所以进来不发瓦片请求)。 */
export const DEFAULT_ZOOM = 11;

/** 列表点击 fitBounds 的最大 zoom, 防止小面被放大到看不清环境。 */
export const FIT_MAX_ZOOM = 18;

/** 属性抽屉宽度 (px)。抽屉是绝对定位盖在地图右侧的, 地图右侧的浮层要按它让位。 */
export const DRAWER_WIDTH = 420;

/**
 * 点击列表行时的落点 zoom。
 * 取 BUILDING_MIN_ZOOM 是为了落地就能同时看到 1 级 + 2 级 (低于它只有 2 级)。
 */
export const LIST_CLICK_ZOOM = BUILDING_MIN_ZOOM;

/**
 * 「飞到坐标」的落点 zoom。
 * 取 18 (> BUILDING_MIN_ZOOM=17): 落地就能看到 1 级楼栋轮廓, 便于对着坐标找楼。
 */
export const COORD_FLY_ZOOM = 18;

/** fitBounds 内边距 (px)。 */
export const FIT_PADDING_PX = 60;

/** 列表单次查询条数 (卡片式列表, 不分页, 一次拉这么多)。 */
export const LIST_PAGE_SIZE = 200;

/**
 * 审核视图 (生效状态选「未生效」/「已生效」) 的单页条数。
 * PRD (V2.1) 定 50, 配后端按 aoiId 升序做稳定翻阅; 「全部」不算审核视图, 维持 LIST_PAGE_SIZE。
 */
export const AUDIT_LIST_PAGE_SIZE = 50;

/**
 * aoiId 归一成数字。
 *
 * <p>后端 GraphQL 的 Long 标量序列化成的是**字符串** (api-server GraphQLScalarConfiguration
 * 里 Coercing&lt;Long, String&gt;), 虽然前端 TS 类型声明写的是 number, 运行时拿到的是 "12345";
 * 而 MVT 瓦片属性里的 aoiId 是真数字。两边混用时任何 `===` / `Set.has` 比对都会静默失配
 * (详情永远对不上号、冲突面永远不标红)。所以 aoiId 参与比对/存状态前必须先过这里。
 * AOI id 是 bigint 自增, 远小于 2^53, Number() 无精度问题。
 */
export function toAoiIdNumber(v: number | string): number {
    return typeof v === 'number' ? v : Number(v);
}

/* ====================== 颜色表 ====================== */

/** 来源分组 (MANUAL=手绘 / SURVEY=实采 / OVERTURE=OVERTURE 系 + OSM + 空)。 */
export type SourceGroup = 'MANUAL' | 'SURVEY' | 'OVERTURE';

/** data_source 原始值 → 来源分组。 */
export function toSourceGroup(dataSource: string | null | undefined): SourceGroup {
    if (dataSource === 'MANUAL') return 'MANUAL';
    if (dataSource === 'SURVEY') return 'SURVEY';
    // OVERTURE / OVERTURE_AGGREGATED / OSM / null / 其他 → 统一归 OVERTURE 组
    return 'OVERTURE';
}

/** 来源分组 → 中文标签 (OVERTURE 系对用户统一叫"导入", 2026-08-04 定)。 */
export const SOURCE_GROUP_LABELS: Record<SourceGroup, string> = {
    MANUAL: '手绘',
    SURVEY: '实采',
    OVERTURE: '导入',
};

/** 颜色 (Leaflet 用 hex, 图例色块也用 rgb, 两份都给)。 */
export interface AoiColor {
    hex: string;
    rgb: [number, number, number];
}

/**
 * 来源分色 (原型图例绿色系为主): 手绘=绿 / 实采=橙 / OVERTURE=灰蓝。
 * SELECTED 蓝只用于"绘制中 / 编辑形状中"的工作态; 已存在面的选中态改走级别加深色
 * (selectedLevelColor), 让选中面仍能一眼看出级别。冲突=红。
 */
export const AOI_COLORS: Record<SourceGroup | 'SELECTED' | 'CONFLICT', AoiColor> = {
    MANUAL: { hex: '#12905a', rgb: [18, 144, 90] },
    SURVEY: { hex: '#d97706', rgb: [217, 119, 6] },
    OVERTURE: { hex: '#5a7d9a', rgb: [90, 125, 154] },
    SELECTED: { hex: '#2f6fed', rgb: [47, 111, 237] },
    CONFLICT: { hex: '#d0393f', rgb: [208, 57, 63] },
};

/**
 * AOI 级别配色 —— 沿用旧「AOI 生产管理」详情页的色板 (aoi-platform/constants.ts AOI_DETAIL_COLORS),
 * 避免两个页面同一份数据两套观感。
 *
 * <p>1级(楼栋) 青、2级(聚合) 紫; 来源(手绘/实采/OVERTURE) 不再用颜色区分, 改由图例的图层开关控制显隐,
 * 因为一屏里"级别"才是要一眼分清的维度, 来源看抽屉标签就够。
 */
export const LEVEL_COLORS: Record<'LEVEL_1' | 'LEVEL_2', AoiColor> = {
    LEVEL_1: { hex: '#06b6d4', rgb: [6, 182, 212] },
    LEVEL_2: { hex: '#8b5cf6', rgb: [139, 92, 246] },
};

/** 级别未知时的兜底描边色 (脏数据, 正常不该出现)。 */
export const LEVEL_UNKNOWN_COLOR: AoiColor = { hex: '#94a3b8', rgb: [148, 163, 184] };

/** 级别枚举 → 颜色; 传 null / 非法值走兜底色。 */
export function levelColor(levelEnum: string | null | undefined): AoiColor {
    if (levelEnum === 'LEVEL_1') return LEVEL_COLORS.LEVEL_1;
    if (levelEnum === 'LEVEL_2') return LEVEL_COLORS.LEVEL_2;
    return LEVEL_UNKNOWN_COLOR;
}

/**
 * 选中态配色 = 级别色加深 (1级 深青 / 2级 深紫), 配合更粗的描边表达"选中"。
 * 不再用独立的蓝色选中态 —— 选中后级别依旧一眼可辨。
 */
export const SELECTED_LEVEL_COLORS: Record<'LEVEL_1' | 'LEVEL_2', AoiColor> = {
    LEVEL_1: { hex: '#0e7490', rgb: [14, 116, 144] },
    LEVEL_2: { hex: '#6d28d9', rgb: [109, 40, 217] },
};

/** 级别枚举 → 选中态颜色; 级别未知 (detail 未回来等) 走深灰兜底。 */
export function selectedLevelColor(levelEnum: string | null | undefined): AoiColor {
    if (levelEnum === 'LEVEL_1') return SELECTED_LEVEL_COLORS.LEVEL_1;
    if (levelEnum === 'LEVEL_2') return SELECTED_LEVEL_COLORS.LEVEL_2;
    return { hex: '#475569', rgb: [71, 85, 105] };
}

/** 进门点 marker 颜色 (原型: 绿)。 */
export const ENTRY_POINT_COLOR: AoiColor = { hex: '#12905a', rgb: [18, 144, 90] };

/** 停车点 marker 颜色 (原型: 蓝)。 */
export const PARKING_POINT_COLOR: AoiColor = { hex: '#2f6fed', rgb: [47, 111, 237] };

/* ====================== UI 主题色 (对齐原型) ====================== */

/**
 * AOI 面的填充不透明度。
 *
 * <p><b>UNVERIFIED 必须严格大于 VERIFIED</b> —— 这是「未生效 = 待办, 要比已生效更抢眼」这条审核约定的
 * 代码化表达 (2026-09-21)。历史上这两个值是反的 (0.05 vs 0.14): 按"还没进业务"的业务语义强弱分配视觉权重,
 * 结果要审的那个被画得比不关心的更弱, 运营反馈"区分起来很累"。审核场景要按任务重要性分配。
 * 想改回去之前先看 __tests__/aoiFaceStyle.test.ts 里的断言。
 */
export const AOI_FILL_OPACITY = {
    /** 未生效 (待审核): 主信号。填充面积是前注意通道, 余光扫一眼整屏成像 */
    UNVERIFIED: 0.35,
    /** 已生效: 退成底图 */
    VERIFIED: 0.14,
    /** 冲突 / 选中 */
    HIGHLIGHT: 0.26,
} as const;

/** AOI 面的描边宽度 (px)。未生效加粗, 让虚线在小面 / 低 zoom 下仍可辨 (否则会糊成实线)。 */
export const AOI_STROKE_WEIGHT = {
    UNVERIFIED: 2.5,
    VERIFIED: 1.5,
    HIGHLIGHT: 3,
} as const;

/** 原型主色 (按钮 / 选中 / 链接)。 */
// THEME 已移除: 页面统一使用 antd 默认 token + tailwind 工具类, 不再自持一套色板。

/* ====================== 后端错误码 → 中文文案 ====================== */

/** BizAoiProduceResultType.errorCode → 用户可读中文提示。 */
export const PRODUCE_ERROR_MESSAGES: Record<string, string> = {
    INVALID_GEOMETRY: '几何不合法 (自相交 / 格式错误), 请调整后重试',
    // VERTEX_LIMIT 故意不在这里: 上限是后端动态配置, 前端不知道当前值, 走 produceErrorText 的兜底用后端
    // errorMessage ("顶点数需在 3~120 之间, 当前 135"), 数字永远是真的
    AREA_LIMIT: 'AOI 面积超出上限 (1级 2km² / 2级 15km²)',
    OVERLAP_SAME_LEVEL: '与同级 AOI 重叠, 冲突面已在地图标红',
    CROSS_OTHER_LEVEL_EDGE: '与其他级别 AOI 边界交叉, 冲突面已在地图标红',
    CENTER_OUT_OF_ZIPCODE: '中心点不在所属 Zipcode 边界内',
    ZIPCODE_LOOKUP_FAILED: '中心点 Zipcode 解析失败, 请稍后重试',
    LOCK_TIMEOUT: '系统繁忙 (锁超时), 请稍后重试',
    AOI_NOT_FOUND: 'AOI 不存在或已被删除',
};

/** 冲突类错误码 (需要标红 conflictAoiIds 并 fitBounds)。 */
export const CONFLICT_ERROR_CODES = new Set(['OVERLAP_SAME_LEVEL', 'CROSS_OTHER_LEVEL_EDGE']);

/** errorCode → 中文; 未知 code 兜底用 errorMessage / 通用文案。 */
export function produceErrorText(errorCode: string | null | undefined, errorMessage: string | null | undefined): string {
    if (errorCode && PRODUCE_ERROR_MESSAGES[errorCode]) return PRODUCE_ERROR_MESSAGES[errorCode];
    return errorMessage || '操作失败, 请稍后重试';
}

/* ====================== 异步任务 ====================== */
